import { useState, useMemo, useEffect, useRef } from "react";
import Select from "react-select";
import countryList from "react-select-country-list";
import { allCountries } from "country-telephone-data";
import {
    FaUser,
    FaPhone,
    FaEnvelope,
    FaVenusMars,
    FaBriefcase,
    FaChartLine,
    FaMapMarkerAlt,
    FaCity,
    FaSave,
    FaCheckCircle,
    FaUpload,
    FaCalendarAlt
} from "react-icons/fa";
import { toast } from "react-toastify";
import { MdPhotoCamera } from "react-icons/md";
import SignatureCanvas from "react-signature-canvas";
import { FaPenFancy } from "react-icons/fa";
import "../styles/SolicitorProfile.css";

import API_BASE_URL from "../config";
import { useAuth } from "../context/AuthContext";

export default function SolicitorProfile() {
    const { user, token, login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });
    const [postcodeQuery, setPostcodeQuery] = useState("");
    const [postcodeSuggestions, setPostcodeSuggestions] = useState([]);
    const [fetchingPostcodes, setFetchingPostcodes] = useState(false);
    const [signatureMode, setSignatureMode] = useState("upload"); // upload | draw
    const [signaturePreview, setSignaturePreview] = useState(user?.profile?.signature || "");
    const sigRef = useRef(null);
    // eslint-disable-next-line no-unused-vars
    const [signatureFileSelected, setSignatureFileSelected] = useState(false);
    const [signatureDrawn, setSignatureDrawn] = useState(false);
    const signatureInputRef = useRef(null);

    /* ======================
       ATTORNEY SELECT STYLES
    ====================== */
    const attorneySelectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: "48px",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            boxShadow: "none",
            backgroundColor: state.isDisabled ? "#f9fafb" : "#ffffff",
            borderColor: state.isFocused ? "#bfa56a" : "#d1d5db",
            "&:hover": {
                borderColor: state.isFocused ? "#bfa56a" : "#9ca3af"
            },
            transition: "all 0.3s ease"
        }),
        menu: (base) => ({
            ...base,
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            zIndex: 1000
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
                ? "rgba(191, 165, 106, 0.1)"
                : state.isFocused
                    ? "rgba(191, 165, 106, 0.05)"
                    : "#ffffff",
            color: state.isSelected ? "#3a2f2c" : "#1f2937",
            fontSize: "14px",
            padding: "10px 16px",
            cursor: "pointer",
            "&:active": {
                backgroundColor: "rgba(191, 165, 106, 0.1)"
            }
        }),
        singleValue: (base) => ({
            ...base,
            color: "#3a2f2c",
            fontSize: "14px",
            fontWeight: "500"
        }),
        placeholder: (base) => ({
            ...base,
            color: "#9ca3af",
            fontSize: "14px"
        }),
        dropdownIndicator: (base) => ({
            ...base,
            color: "#6b7280",
            "&:hover": {
                color: "#3a2f2c"
            }
        }),
        indicatorSeparator: (base) => ({
            ...base,
            backgroundColor: "#e5e7eb"
        })
    };

    /* ======================
       COUNTRY OPTIONS
    ====================== */
    const countryOptions = useMemo(() => {
        return countryList()
            .getData()
            .map((c) => {
                const tel = allCountries.find(
                    (t) => t.iso2 === c.value.toLowerCase()
                );
                // Remove native language from name
                const cleanName = c.label.replace(/\s*\(.*?\)\s*/g, "");
                return {
                    label: `${cleanName} (${tel ? "+" + tel.dialCode : ""})`,
                    value: tel ? "+" + tel.dialCode : "",
                    flag: c.value.toLowerCase(),
                    name: cleanName
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    }, []);

    /* ======================
       FORM STATE
    ====================== */
    const [form, setForm] = useState({
        name: "",
        countryCode: "+44",
        mobile: "",
        profile: {
            photo: "",
            gender: "",
            advocateLevel: "",
            experience: "",
            totalCases: 0,          // ✅ NEW
            consultationFee: "",
            address: {
                city: "",
                location: "",
                postcode: ""
            }
        }
    });

    const GEOAPIFY_API_KEY = "bf58eac2ef4d43d3b7c5661f657f2a03";

    // Initialize form with user data
    useEffect(() => {
        if (user) {
            setForm({
                name: user.name || "",
                countryCode: user.countryCode || "+44",
                mobile: user.mobile || "",
                profile: {
                    photo: user.profile?.photo || "",
                    gender: user.profile?.gender || "",
                    advocateLevel: user.profile?.advocateLevel || "",
                    totalCases: user.profile?.totalCases || 0,
                    consultationFee: user.profile?.consultationFee || "",
                    experience: user.profile?.experience || "",
                    address: {
                        city: user.profile?.address?.city || "",
                        location: user.profile?.address?.location || "",
                        postcode: user.profile?.address?.postcode || ""
                    }
                }
            });
            setCityQuery(user.profile?.address?.city || "");
            setPostcodeQuery(user.profile?.address?.postcode || "");
        }
    }, [user]);

    const fetchPostcodes = async (value) => {
        setPostcodeQuery(value);

        if (value.length < 2) {
            setPostcodeSuggestions([]);
            return;
        }

        setFetchingPostcodes(true);
        try {
            const res = await fetch(
                `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
                    value
                )}&type=postcode&limit=5&apiKey=${GEOAPIFY_API_KEY}`
            );

            const data = await res.json();

            const postcodes =
                data.features?.map((item) => ({
                    id: item.properties.place_id,
                    postcode: item.properties.postcode || item.properties.name,
                    city: item.properties.city || "",
                    country: item.properties.country || "",
                    label: `${item.properties.postcode || item.properties.name}, ${item.properties.country}`
                })) || [];

            setPostcodeSuggestions(postcodes);
        } catch (error) {
            console.error("Geoapify postcode fetch error:", error);
        } finally {
            setFetchingPostcodes(false);
        }
    };



    /* ======================
       CITY AUTOCOMPLETE
    ====================== */
    const [cityQuery, setCityQuery] = useState("");
    const [citySuggestions, setCitySuggestions] = useState([]);
    const [fetchingCities, setFetchingCities] = useState(false);

    const fetchCities = async (value) => {
        setCityQuery(value);

        if (value.length < 2) {
            setCitySuggestions([]);
            return;
        }

        setFetchingCities(true);
        try {
            const res = await fetch(
                `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
                    value
                )}&type=city&limit=5&apiKey=${GEOAPIFY_API_KEY}`
            );

            const data = await res.json();

            const cities =
                data.features?.map((item) => ({
                    id: item.properties.place_id,
                    city: item.properties.city || item.properties.name,
                    state: item.properties.state || "",
                    country: item.properties.country || "",
                    label: `${item.properties.city || item.properties.name}, ${item.properties.country}`
                })) || [];

            setCitySuggestions(cities);
        } catch (error) {
            console.error("Geoapify city fetch error:", error);
        } finally {
            setFetchingCities(false);
        }
    };


    /* ======================
       PHOTO UPLOAD
    ====================== */
    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Accept ALL image formats
        if (!file.type.startsWith("image/")) {
            setMessage({
                type: "error",
                text: "Please upload a valid image file"
            });
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append("photo", file);

        try {
            const res = await fetch(
                `${API_BASE_URL}/api/auth/solicitor/upload-photo`,
                {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` },
                    body: formData
                }
            );

            const data = await res.json();
            if (res.ok) {
                setForm({
                    ...form,
                    profile: { ...form.profile, photo: data.photo }
                });
                setMessage({
                    type: "success",
                    text: "Profile photo updated successfully"
                });
            } else {
                setMessage({
                    type: "error",
                    text: data.message || "Failed to upload photo"
                });
            }
        } catch (error) {
            setMessage({
                type: "error",
                text: "Error uploading photo"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSignatureUpload = async (file) => {
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            toast.error("Please upload a valid signature image");
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append("signature", file);

        try {
            const res = await fetch(
                `${API_BASE_URL}/api/auth/solicitor/upload-signature`,
                {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` },
                    body: formData
                }
            );

            const data = await res.json();

            if (res.ok) {
                setSignaturePreview(data.signature);
                login(token, data.user);

                toast.success("Digital signature uploaded successfully");

                setSignatureFileSelected(false);
                setSignatureDrawn(false);
            } else {
                toast.error(data.message || "Failed to upload signature");
            }
        } catch (err) {
            toast.error("Error uploading signature");
        } finally {
            setLoading(false);
        }
    };



    /* ======================
       SAVE PROFILE
    ====================== */
    const saveProfile = async () => {
        if (!form.name.trim()) {
            toast.warning("Full name is required");
            return;
        }

        if (!form.mobile.trim()) {
            toast.warning("Mobile number is required");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(
                `${API_BASE_URL}/api/auth/solicitor/profile`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify(form)
                }
            );

            const data = await res.json();

            if (res.ok) {
                login(token, data.user);

                toast.success("Profile updated successfully");
            } else {
                toast.error(data.message || "Failed to update profile");
            }
        } catch (error) {
            toast.error("Error updating profile");
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="attorney-profile-container">
            {/* Header */}
            <div className="attorney-profile-header">
                <div className="attorney-header-content">
                    <div>
                        <h1 className="attorney-title">Attorney Profile</h1>
                        <p className="attorney-subtitle">Manage your professional information and credentials</p>
                    </div>
                </div>
            </div>


            {/* Message Alert */}
            {message.text && (
                <div className={`attorney-message attorney-message-${message.type}`}>
                    <div className="attorney-message-content">
                        {message.type === "success" ? <FaCheckCircle size={18} /> : "!"}
                        <span>{message.text}</span>
                    </div>
                    <button
                        className="attorney-message-close"
                        onClick={() => setMessage({ type: "", text: "" })}
                    >
                        ×
                    </button>
                </div>
            )}

            <div className="attorney-profile-content">
                {/* Left Column - Profile Image & Quick Info */}
                <div className="attorney-profile-sidebar">
                    {/* Profile Image Card */}
                    <div className="attorney-image-card">

                        {/* PROFILE PHOTO */}
                        <div className="attorney-image-wrapper">
                            {form.profile.photo ? (
                                <img
                                    src={form.profile.photo}
                                    alt="Profile"
                                    className="attorney-avatar"
                                />
                            ) : (
                                <div className="attorney-avatar-placeholder">
                                    <FaUser size={40} color="#bfa56a" />
                                </div>
                            )}
                            <div className="attorney-image-overlay">
                                <label htmlFor="attorney-profile-upload" className="attorney-upload-label">
                                    <MdPhotoCamera size={20} />
                                    <span>Change Photo</span>
                                </label>
                                <input
                                    id="attorney-profile-upload"
                                    type="file"
                                    accept="image/*"
                                    onChange={handlePhotoUpload}
                                    className="attorney-upload-input"
                                    disabled={loading}
                                />
                            </div>

                        </div>
                        {user?.profile?.expertBadge && (
                            <div className="attorney-expert-badge">
                                <FaCheckCircle size={14} />
                                <span>Expert Solicitor</span>
                            </div>
                        )}


                        {/* PHOTO UPLOAD BUTTON */}
                        <div className="attorney-upload-actions">
                            <button
                                onClick={handlePhotoUpload}
                                disabled={loading || !document.getElementById('attorney-profile-upload')?.files?.length}
                                className="attorney-upload-btn"
                            >
                                <FaUpload size={16} />
                                <span>{loading ? "Uploading..." : "Upload Photo"}</span>
                            </button>
                        </div>

                        {/* 🔥 DIGITAL SIGNATURE SECTION */}
                        <div className="attorney-signature-section">

                            <h4 className="attorney-signature-title">Digital Signature</h4>

                            {/* Hidden File Input */}
                            <input
                                ref={signatureInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                disabled={loading}
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        handleSignatureUpload(file);
                                    }
                                }}
                            />

                            {/* Signature Preview Circle */}
                            <div
                                className="attorney-signature-wrapper"
                                style={{ cursor: "pointer" }}
                            >
                                {signaturePreview ? (
                                    <img
                                        src={signaturePreview}
                                        alt="Signature"
                                        className="attorney-avatar attorney-signature-avatar"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(signaturePreview, "_blank");   // 🔍 open full preview
                                        }}
                                    />
                                ) : (
                                    <div
                                        className="attorney-avatar-placeholder attorney-signature-placeholder"
                                        onClick={() => signatureInputRef.current.click()}   // 🔥 open picker
                                    >
                                        <FaPenFancy size={32} color="#bfa56a" />
                                    </div>
                                )}

                                {/* 🔥 OVERLAY — ALWAYS SHOW ON HOVER */}
                                <div
                                    className="attorney-image-overlay"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        signatureInputRef.current.click();   // 🔥 change / upload again
                                    }}
                                >
                                    <span>{signaturePreview ? "Change Signature" : "Upload Signature"}</span>
                                </div>
                            </div>

                            {/* MODE TABS */}
                            <div className="attorney-signature-tabs">
                                {!signaturePreview && (
                                    <button
                                        className={signatureMode === "upload" ? "active" : ""}
                                        onClick={() => setSignatureMode("upload")}
                                    >
                                        Upload
                                    </button>
                                )}

                                <button
                                    className={signatureMode === "draw" ? "active" : ""}
                                    onClick={() => setSignatureMode("draw")}
                                >
                                    Draw
                                </button>
                            </div>

                            {/* DRAW MODE */}
                            {/* DRAW MODE */}
                            {signatureMode === "draw" && (
                                <div className="attorney-signature-draw">

                                    {/* 🔥 HEADER WITH CLOSE BUTTON */}
                                    <div className="attorney-signature-draw-header">
                                        <span>Draw your signature</span>

                                        <button
                                            type="button"
                                            className="sig-close"
                                            onClick={() => {
                                                sigRef.current.clear();
                                                setSignatureDrawn(false);
                                                setSignatureMode("upload");   // 🔥 close draw mode
                                            }}
                                        >
                                            ✕
                                        </button>
                                    </div>

                                    <SignatureCanvas
                                        ref={sigRef}
                                        penColor="black"
                                        onEnd={() => setSignatureDrawn(true)}
                                        canvasProps={{
                                            width: 260,
                                            height: 120,
                                            className: "attorney-signature-canvas"
                                        }}
                                    />

                                    <div className="attorney-signature-actions">
                                        <button
                                            onClick={() => {
                                                sigRef.current.clear();
                                                setSignatureDrawn(false);
                                            }}
                                            className="sig-clear"
                                        >
                                            Clear
                                        </button>

                                        <button
                                            disabled={!signatureDrawn || loading}
                                            onClick={async () => {
                                                const image = sigRef.current.toDataURL("image/png");

                                                const res = await fetch(image);
                                                const blob = await res.blob();
                                                const file = new File([blob], "signature.png", { type: "image/png" });

                                                handleSignatureUpload(file);
                                            }}
                                            className="sig-save"
                                            style={{
                                                opacity: !signatureDrawn ? 0.5 : 1,
                                                cursor: !signatureDrawn ? "not-allowed" : "pointer"
                                            }}
                                        >
                                            Save Signature
                                        </button>
                                    </div>

                                </div>
                            )}


                        </div>






                        {/* QUICK INFO (UNCHANGED) */}
                        <div className="attorney-quick-info">
                            <div className="attorney-info-item">
                                <span className="attorney-info-label">Account Status</span>
                                <span className="attorney-info-value attorney-status-active">
                                    Active
                                </span>
                            </div>

                            <div className="attorney-info-item">
                                <span className="attorney-info-label">SRA Verified</span>
                                <span className="attorney-info-value attorney-status-verified">
                                    {user?.sraNumber ? "Verified" : "Pending"}
                                </span>
                            </div>

                            <div className="attorney-info-item">
                                <span className="attorney-info-label">Member Since</span>
                                <span className="attorney-info-value">
                                    {user?.createdAt
                                        ? new Date(user.createdAt).toLocaleDateString("en-GB", {
                                            year: "numeric",
                                            month: "short"
                                        })
                                        : "N/A"}
                                </span>
                            </div>
                        </div>

                    </div>
                </div>



                {/* Right Column - Profile Form */}
                <div className="attorney-profile-main">
                    {/* Personal Information Card */}
                    <div className="attorney-form-card">
                        <div className="attorney-form-card-header">
                            <div className="attorney-form-card-icon">
                                <FaUser size={20} />
                            </div>
                            <h3 className="attorney-form-card-title">Personal Information</h3>
                        </div>

                        <div className="attorney-profile-form">
                            <div className="attorney-form-row">
                                <div className="attorney-form-group">
                                    <label className="attorney-form-label">
                                        <FaUser size={14} />
                                        <span>Full Name</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="attorney-form-input"
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        placeholder="Enter your full name"
                                    />
                                </div>

                                <div className="attorney-form-group">
                                    <label className="attorney-form-label">
                                        <FaEnvelope size={14} />
                                        <span>Email Address</span>
                                    </label>
                                    <input
                                        type="email"
                                        className="attorney-form-input attorney-form-input-disabled"
                                        value={user?.email || ""}
                                        disabled
                                    />
                                </div>
                            </div>

                            <div className="attorney-form-row">
                                {/* Country Code Select */}
                                <div className="attorney-form-group">
                                    <label className="attorney-form-label">
                                        <FaPhone size={14} />
                                        <span>Country Code</span>
                                    </label>
                                    <Select
                                        options={countryOptions}
                                        value={countryOptions.find((c) => c.value === form.countryCode)}
                                        onChange={(selected) => setForm({ ...form, countryCode: selected.value })}
                                        styles={attorneySelectStyles}
                                        placeholder="Select country code"
                                        classNamePrefix="attorney-select"
                                        isSearchable
                                        menuPlacement="auto"
                                    />
                                </div>

                                {/* Mobile Number Input */}
                                <div className="attorney-form-group">
                                    <label className="attorney-form-label">
                                        <FaPhone size={14} />
                                        <span>Mobile Number</span>
                                    </label>
                                    <input
                                        type="tel"
                                        className="attorney-form-input"
                                        value={form.mobile}
                                        onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                                        placeholder="Enter mobile number"
                                    />
                                </div>
                            </div>

                            {/* SRA Number (Read-only) */}
                            {user?.role === "SOLICITOR" && user?.sraNumber && (
                                <div className="attorney-form-row">
                                    <div className="attorney-form-group">
                                        <label className="attorney-form-label">
                                            <FaBriefcase size={14} />
                                            <span>SRA Number</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="attorney-form-input attorney-form-input-disabled"
                                            value={user.sraNumber}
                                            disabled
                                        />
                                        <div className="attorney-field-hint">
                                            SRA Number is verified and cannot be changed
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Professional Information Card */}
                    <div className="attorney-form-card">
                        <div className="attorney-form-card-header">
                            <div className="attorney-form-card-icon">
                                <FaBriefcase size={20} />
                            </div>
                            <h3 className="attorney-form-card-title">Professional Information</h3>
                        </div>

                        <div className="attorney-profile-form">
                            <div className="attorney-form-row">
                                <div className="attorney-form-group">
                                    <label className="attorney-form-label">
                                        <FaVenusMars size={14} />
                                        <span>Gender</span>
                                    </label>
                                    <select
                                        className="attorney-form-input attorney-form-select"
                                        value={form.profile.gender}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                profile: { ...form.profile, gender: e.target.value }
                                            })
                                        }
                                    >
                                        <option value="">Select Gender</option>
                                        <option value="MALE">Male</option>
                                        <option value="FEMALE">Female</option>
                                        <option value="OTHER">Other</option>
                                        <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
                                    </select>
                                </div>

                                <div className="attorney-form-group">
                                    <label className="attorney-form-label">
                                        <FaBriefcase size={14} />
                                        <span>Advocate Level</span>
                                    </label>
                                    <select
                                        className="attorney-form-input attorney-form-select"
                                        value={form.profile.advocateLevel}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                profile: { ...form.profile, advocateLevel: e.target.value }
                                            })
                                        }
                                    >
                                        <option value="">Select Level</option>
                                        <option value="JUNIOR">Junior Advocate</option>
                                        <option value="MID">Mid Level Advocate</option>
                                        <option value="SENIOR">Senior Advocate</option>
                                    </select>
                                </div>
                            </div>

                            <div className="attorney-form-row">
                                <div className="attorney-form-group">
                                    <label className="attorney-form-label">
                                        <FaChartLine size={14} />
                                        <span>Experience (Years)</span>
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="50"
                                        className="attorney-form-input"
                                        value={form.profile.experience}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                profile: { ...form.profile, experience: e.target.value }
                                            })
                                        }
                                        placeholder="e.g., 5"
                                    />
                                </div>

                                <div className="attorney-form-group">
                                    <label className="attorney-form-label">
                                        <FaCheckCircle size={14} />
                                        <span>Total Successful Cases</span>
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        className="attorney-form-input"
                                        value={form.profile.totalCases}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                profile: {
                                                    ...form.profile,
                                                    totalCases: e.target.value
                                                }
                                            })
                                        }
                                        placeholder="e.g., 120"
                                    />
                                </div>

                            </div>
                            <div className="attorney-form-group">
                                <label className="attorney-form-label">
                                    <FaCalendarAlt size={14} />
                                    <span>Consultation Fee</span>
                                </label>
                                <div className="attorney-input-prefix">
                                    <span className="currency-prefix">£</span>
                                    <input
                                        type="number"
                                        min="0"
                                        className="attorney-form-input"
                                        value={form.profile.consultationFee}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                profile: {
                                                    ...form.profile,
                                                    consultationFee: e.target.value
                                                }
                                            })
                                        }
                                        placeholder="e.g., 50"
                                    />
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Location Information Card */}
                    <div className="attorney-form-card">
                        <div className="attorney-form-card-header">
                            <div className="attorney-form-card-icon">
                                <FaMapMarkerAlt size={20} />
                            </div>
                            <h3 className="attorney-form-card-title">Location Information</h3>
                        </div>

                        <div className="attorney-profile-form">

                            {/* Row 1 — City + Postcode */}
                            <div className="attorney-form-row">

                                {/* City */}
                                <div className="attorney-form-group">
                                    <label className="attorney-form-label">
                                        <FaCity size={14} />
                                        <span>City</span>
                                    </label>

                                    <div className="attorney-autocomplete-wrapper">
                                        <input
                                            type="text"
                                            className="attorney-form-input"
                                            value={cityQuery}
                                            onChange={(e) => fetchCities(e.target.value)}
                                            placeholder="Start typing to search cities..."
                                        />

                                        {fetchingCities && (
                                            <div className="attorney-loading-spinner">Searching cities...</div>
                                        )}

                                        {citySuggestions.length > 0 && (
                                            <ul className="attorney-suggestions-list">
                                                {citySuggestions.map((city) => (
                                                    <li
                                                        key={city.id}
                                                        className="attorney-suggestion-item"
                                                        onClick={() => {
                                                            setForm({
                                                                ...form,
                                                                profile: {
                                                                    ...form.profile,
                                                                    address: {
                                                                        ...form.profile.address,
                                                                        city: city.city
                                                                    }
                                                                }
                                                            });
                                                            setCityQuery(city.city);
                                                            setCitySuggestions([]);
                                                        }}
                                                    >
                                                        {city.label}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>

                                {/* Postcode */}
                                <div className="attorney-form-group">
                                    <label className="attorney-form-label">
                                        <FaMapMarkerAlt size={14} />
                                        <span>Postcode</span>
                                    </label>

                                    <div className="attorney-autocomplete-wrapper">
                                        <input
                                            type="text"
                                            className="attorney-form-input"
                                            value={postcodeQuery}
                                            onChange={(e) => {
                                                const value = e.target.value;

                                                setPostcodeQuery(value);
                                                fetchPostcodes(value);

                                                setForm({
                                                    ...form,
                                                    profile: {
                                                        ...form.profile,
                                                        address: {
                                                            ...form.profile.address,
                                                            postcode: value
                                                        }
                                                    }
                                                });
                                            }}
                                            placeholder="Enter postcode / ZIP / PIN"
                                        />

                                        {fetchingPostcodes && (
                                            <div className="attorney-loading-spinner">Searching postcodes...</div>
                                        )}

                                        {postcodeSuggestions.length > 0 && (
                                            <ul className="attorney-suggestions-list">
                                                {postcodeSuggestions.map((p) => (
                                                    <li
                                                        key={p.id}
                                                        className="attorney-suggestion-item"
                                                        onClick={() => {
                                                            setForm({
                                                                ...form,
                                                                profile: {
                                                                    ...form.profile,
                                                                    address: {
                                                                        ...form.profile.address,
                                                                        postcode: p.postcode
                                                                    }
                                                                }
                                                            });
                                                            setPostcodeQuery(p.postcode);
                                                            setPostcodeSuggestions([]);
                                                        }}
                                                    >
                                                        {p.label}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>

                            </div>

                            {/* Row 2 — Location / Address (Full Width) */}
                            <div className="attorney-form-row attorney-form-row-full">

                                <div className="attorney-form-group">
                                    <label className="attorney-form-label">
                                        <FaMapMarkerAlt size={14} />
                                        <span>Location / Address</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="attorney-form-input"
                                        value={form.profile.address.location}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                profile: {
                                                    ...form.profile,
                                                    address: {
                                                        ...form.profile.address,
                                                        location: e.target.value
                                                    }
                                                }
                                            })
                                        }
                                        placeholder="Enter your complete address"
                                    />
                                </div>

                            </div>

                        </div>
                    </div>


                    {/* Action Buttons */}
                    <div className="attorney-profile-actions">
                        <button
                            className="attorney-save-btn"
                            onClick={saveProfile}
                            disabled={loading}
                        >
                            <FaSave className="attorney-btn-icon" />
                            <span>{loading ? "Saving..." : "Save Profile"}</span>
                            {loading && <div className="attorney-spinner"></div>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}